import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Download, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AssessmentReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentData: any;
}

const AssessmentReportModal = ({ open, onOpenChange, assessmentData }: AssessmentReportModalProps) => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: "all",
    includeCharts: true,
    includeRecommendations: true,
    includeTimeline: true,
    format: "pdf"
  });

  const handleDownload = async () => {
    try {
      setIsGenerating(true);

      // Generate report based on filters
      const reportData = {
        ...assessmentData,
        filters,
        generatedAt: new Date().toISOString(),
      };

      // Call the report generation service
      const response = await generateAssessmentReport(reportData);
      
      // Create download link
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessment-report-${new Date().toISOString()}.${filters.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Report Generated",
        description: "Your assessment report has been downloaded successfully.",
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate report. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Generate Assessment Report</DialogTitle>
          <DialogDescription>
            Customize your report content and format
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Date Range */}
          <div className="space-y-2">
            <Label>Date Range</Label>
            <Select
              value={filters.dateRange}
              onValueChange={(value) => setFilters(prev => ({ ...prev, dateRange: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                <SelectItem value="last_year">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Report Format */}
          <div className="space-y-2">
            <Label>Report Format</Label>
            <Select
              value={filters.format}
              onValueChange={(value) => setFilters(prev => ({ ...prev, format: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF Document</SelectItem>
                <SelectItem value="xlsx">Excel Spreadsheet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Content Options */}
          <div className="space-y-4">
            <Label>Include in Report</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="charts"
                  checked={filters.includeCharts}
                  onCheckedChange={(checked) => 
                    setFilters(prev => ({ ...prev, includeCharts: checked as boolean }))
                  }
                />
                <Label htmlFor="charts">Charts and Visualizations</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="recommendations"
                  checked={filters.includeRecommendations}
                  onCheckedChange={(checked) => 
                    setFilters(prev => ({ ...prev, includeRecommendations: checked as boolean }))
                  }
                />
                <Label htmlFor="recommendations">Recommendations</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="timeline"
                  checked={filters.includeTimeline}
                  onCheckedChange={(checked) => 
                    setFilters(prev => ({ ...prev, includeTimeline: checked as boolean }))
                  }
                />
                <Label htmlFor="timeline">Timeline and Next Steps</Label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download Report
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssessmentReportModal;